import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { parseFractionLikeInput, formatLatexPreview } from "../../../../components/inputs/FractionInput";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function formatNumber(value) {
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(4))).replace(".", ",");
}

function almostEqual(a, b) {
  return Math.abs(a - b) < 1e-9;
}

function getRaw(userAnswer) {
  if (typeof userAnswer === "string") return userAnswer;
  if (!userAnswer) return "";
  if (typeof userAnswer.raw === "string") return userAnswer.raw;
  if (typeof userAnswer.value === "string") return userAnswer.value;
  return "";
}

function formatUserLatex(raw) {
  return formatLatexPreview(raw) || String(raw || "").trim() || "\\text{brak}";
}

function unitLatex(task) {
  return task?.unitLatex ? String.raw`\ \text{${task.unitLatex}}` : "";
}

export function createTask(type, templates, options = {}) {
  const template = options.template || pick(templates);
  const data = typeof template.build === "function" ? template.build(options) : template;
  return {
    type,
    ...data,
    tags: ["algebra", "wzory", "zaleznosci", "podstawianie_do_wzorow", ...(data.tags || [])],
    maxPoints: data.maxPoints || 1,
  };
}

export function renderSubstitutionTask(task) {
  return (
    <div>
      <p>{task.prompt}</p>
      <BlockMath math={task.formulaLatex} />
      <p>
        Dane: <InlineMath math={task.valuesLatex} />
      </p>
    </div>
  );
}

export function renderSubstitutionInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={task.answerLabelLatex || task.answerSymbol || "x"}
      suffixLatex={task.unitLatex ? String.raw`\text{${task.unitLatex}}` : ""}
      placeholder={task.placeholder || "np. 12"}
    />
  );
}

export function checkSubstitutionAnswer(task, userAnswer) {
  const raw = getRaw(userAnswer);
  const parsed = parseFractionLikeInput(raw);
  const correct = Number.isFinite(parsed) && almostEqual(parsed, task.answer);
  return {
    correct,
    points: correct ? task.maxPoints || 1 : 0,
    maxPoints: task.maxPoints || 1,
  };
}

export function renderSubstitutionAnswer(task) {
  return <InlineMath math={`${task.answerLabelLatex || task.answerSymbol}=${formatNumber(task.answer)}${unitLatex(task)}`} />;
}

export function renderSubstitutionUserAnswer(userAnswer, task) {
  const raw = getRaw(userAnswer);
  return <InlineMath math={`${task?.answerLabelLatex || task?.answerSymbol || "x"}=${formatUserLatex(raw)}${unitLatex(task)}`} />;
}

export function renderSubstitutionExplanation(task) {
  return (
    <div>
      <p><strong>Krok 1.</strong> Podstawiamy dane do wzoru.</p>
      <BlockMath math={task.substitutionLatex} />
      <p><strong>Krok 2.</strong> Obliczamy wartość wyrażenia.</p>
      <BlockMath math={`${task.answerLabelLatex || task.answerSymbol}=${task.resultLatex || formatNumber(task.answer)}${unitLatex(task)}`} />
      <p><strong>Odpowiedź:</strong> <InlineMath math={`${task.answerLabelLatex || task.answerSymbol}=${formatNumber(task.answer)}${unitLatex(task)}`} />.</p>
    </div>
  );
}

export function makeSubstitutionModule(type, templates) {
  return {
    generateTask: (options = {}) => createTask(type, templates, options),
    renderTask: renderSubstitutionTask,
    renderInput: renderSubstitutionInput,
    checkAnswer: checkSubstitutionAnswer,
    renderAnswer: renderSubstitutionAnswer,
    renderUserAnswer: renderSubstitutionUserAnswer,
    renderExplanation: renderSubstitutionExplanation,
  };
}